import {
  COUNTER_TYPES,
  CreateContractRequestBody,
  CUSTOMER_TYPES,
  getProductFromProductCode,
  METER_TYPES,
  OPT_IN_POINTS,
  REGIONS,
  SIGN_CHANNELS,
  SIMULATION_TYPES,
  SITUATIONS,
  SOURCE_TYPES
} from '@boltenergy-be/shared'
import { store } from 'store/index.ts'
import { AddContractData, AddContractSteps, SimulationSalesOffice } from 'features/contracts/add/types.ts'
import dayjs from 'dayjs'
import { fileToBase64 } from 'utils/files.ts'
import { removeNonAlphanumericals } from 'utils/format.ts'
import { checkIsDynamicTariffEligible } from 'utils/smartBillingOptions.ts'
import { ISO8601_DATE_FORMAT, PARTNER_IDS } from 'constants/constants.ts'
import { UserTypes } from 'store/auth/types.ts'
import { IS_PROD_OR_BETA } from 'constants/envs.ts'
import { checkHasMeterAndNumberInputs } from 'utils/contracts.ts'
import { AllProducts } from 'types/products.ts'
import { getLowerCaseLanguage } from 'utils/language.ts'

/**
 * Return number if value is given or 0 as fallback
 * @param {number | string | null | undefined} index
 * @returns {number}
 */
export const formatIndex = (index: number | string | null | undefined): number => {
  if (typeof index !== 'number' && typeof index !== 'string') {
    return 0
  }

  if (typeof index === 'string' && index.match(/[^0-9.]+/)) {
    throw new Error('Index should not contain letters.')
  }

  const numberIndex = Number(index)

  return isNaN(numberIndex) ? 0 : numberIndex
}

export const parseCreateContractData = async (
  data: AddContractData,
  allProducts: AllProducts,
  skipCreditCheck = false
): Promise<CreateContractRequestBody> => {
  // Get data from store
  const { userType } = store.getState().auth
  const { move } = store.getState().app

  // Get data from passed props
  const { address, producer, instalmentData, meterDetails, customerData, referral, product } = data

  // Check if producer is defined
  if (!producer) throw new Error('No producer defined')

  if (address.needsGas && !meterDetails.gas?.ean) throw new Error('Gas EAN is required when gas is needed')

  // Check if we should pass indexes
  const shouldPassIndexesAndMeterNumbers = checkHasMeterAndNumberInputs({
    situation: meterDetails.situation,
    counterType: meterDetails.counterType,
    contractStartDate: meterDetails.contractStartDate
  })

  // Constants
  const lowerCaseLanguage = getLowerCaseLanguage(customerData.language)
  const customerType = customerData.isCompany ? CUSTOMER_TYPES.professional : CUSTOMER_TYPES.residential
  const correspondenceAddress =
    !customerData.sameCorrespondenceAddress && !!customerData.correspondenceAddress
      ? customerData.correspondenceAddress
      : address.deliveryAddress

  // Map & return the create contract data
  return {
    account: {
      customerNumber: customerData.customerNumber,
      birthDate: customerData?.birthDate || undefined,
      company:
        customerData.isCompany && customerData.company
          ? {
              ...customerData.company,
              creditCheck: !skipCreditCheck
            }
          : undefined,
      correspondenceAddress: {
        ...correspondenceAddress,
        streetBox: correspondenceAddress.streetBox || undefined,
        postalCode: Number(correspondenceAddress.postalCode)
      },
      paymentDetails: {
        ibanNumber: customerData.iban ? removeNonAlphanumericals(customerData.iban) : undefined,
        paymentMethod: customerData.paymentMethod
      }
    },
    contact: {
      contactMode: customerData.contactMode,
      email: customerData.email,
      firstName: customerData.firstName,
      language: customerData.language,
      lastName: customerData.lastName,
      phoneMobile: customerData.phoneMobile
    },
    contract: {
      deliveryAddress: {
        ...address.deliveryAddress,
        streetBox: address.deliveryAddress.streetBox || undefined,
        postalCode: Number(address.deliveryAddress.postalCode)
      },
      dynamicTariff: checkIsDynamicTariffEligible({
        meterDetails,
        needsGas: address.needsGas,
        product: getProductFromProductCode(data[AddContractSteps.PRODUCT].electricity),
        region: address.region
      })
        ? meterDetails.dynamicTariff
        : false,
      prices:
        instalmentData.simulationType === SIMULATION_TYPES.boltGo || instalmentData.simulationType === SIMULATION_TYPES.fixedAmount
          ? {
              boltGo:
                instalmentData.simulationType === SIMULATION_TYPES.boltGo
                  ? {
                      electricity: data[AddContractSteps.INSTALMENT_DATA].instalments.electricity,
                      gas: data[AddContractSteps.ADDRESS].needsGas ? data[AddContractSteps.INSTALMENT_DATA].instalments.gas : 0
                    }
                  : undefined,
              fixed:
                instalmentData.simulationType === SIMULATION_TYPES.fixedAmount
                  ? {
                      electricity: Number(instalmentData.instalments.electricity),
                      ...(address.needsGas && { gas: Number(instalmentData.instalments.gas) })
                    }
                  : undefined
            }
          : {},
      priceListUrl: {
        electricity: allProducts.electricity?.[product.electricity]?.pricelists?.[customerType]?.[lowerCaseLanguage],
        gas: address.needsGas && product.gas ? allProducts.gas?.[product.gas]?.pricelists?.[customerType]?.[lowerCaseLanguage] : undefined
      },
      producerId: producer.id,
      products: {
        electricity: data[AddContractSteps.PRODUCT].electricity,
        gas: data[AddContractSteps.PRODUCT].gas
      },
      moveReferral: referral,
      settlementFrequency: meterDetails.settlementFrequency,
      situation: meterDetails.situation,
      startDate: dayjs(meterDetails.contractStartDate).format(ISO8601_DATE_FORMAT),
      usage: {
        // hardcoded values because the backend expects usage.
        // Because portal only support EAV, Fixed Amount & Bolt Go, the usage is not processed in the backend.
        electricity: {
          consumption: {
            single: meterDetails.meterType === METER_TYPES.single ? 1 : undefined,
            double: meterDetails.meterType === METER_TYPES.double ? { day: 1, night: 1 } : undefined
          },
          injection: meterDetails.hasSolarPanels
            ? {
                single: meterDetails.meterType === METER_TYPES.single ? 1 : undefined,
                double: meterDetails.meterType === METER_TYPES.double ? { day: 1, night: 1 } : undefined
              }
            : undefined
        },
        gas: address.needsGas ? 1 : undefined
      },
      uploadDataUrls: {
        energyTransferDocument: meterDetails?.docUpload ? ((await fileToBase64(meterDetails.docUpload[0])) as string) : undefined
      }
    },
    marketing: {
      partnerID:
        userType === UserTypes.SUPER_USER
          ? IS_PROD_OR_BETA
            ? PARTNER_IDS.PRODUCTION_BOLT_SUPER_USER
            : PARTNER_IDS.STAGING_BOLT_SUPER_USER
          : undefined,
      simulationType: instalmentData.simulationType,
      eanCaptureType: meterDetails.eanCaptureType,
      signChannel: userType !== UserTypes.CUSTOMER ? SIGN_CHANNELS.confirmationMail : SIGN_CHANNELS.none,
      optinPoint: OPT_IN_POINTS.portalRegistration,
      simulationSalesOffice: meterDetails.situation === SITUATIONS.move ? SimulationSalesOffice.MOVE : SimulationSalesOffice.EXTRA_ADDRESS,
      sourceType: SOURCE_TYPES.pull,
      moveFlow: move.flow
    },
    meter: {
      counterType: meterDetails.counterType,
      meterType: meterDetails.meterType,
      electricity: {
        ean: meterDetails.electricity.ean,
        exclusiveNightEan:
          meterDetails?.hasExclNight && meterDetails?.hasExclNightEan ? (meterDetails.electricity.eanExclNight ?? undefined) : undefined,
        indexes: shouldPassIndexesAndMeterNumbers
          ? {
              single: meterDetails.meterType === METER_TYPES.single ? formatIndex(meterDetails.electricity.total) : undefined,
              double:
                meterDetails.meterType === METER_TYPES.double
                  ? {
                      day: formatIndex(meterDetails.electricity.day),
                      night: formatIndex(meterDetails.electricity.night)
                    }
                  : undefined,
              exclusiveNight: meterDetails?.hasExclNight ? formatIndex(meterDetails.electricity.exclNight) : undefined
            }
          : undefined,
        meterNumber: shouldPassIndexesAndMeterNumbers ? (meterDetails.electricity.meterNumber ?? undefined) : undefined
      },
      gas:
        address.needsGas && meterDetails.gas
          ? {
              ean: meterDetails.gas.ean,
              // TODO: check type
              // @ts-expect-error
              meterNumber: shouldPassIndexesAndMeterNumbers ? (meterDetails.gas?.meterNumber ?? undefined) : undefined,
              index: shouldPassIndexesAndMeterNumbers ? formatIndex(meterDetails.gas?.total) : undefined
            }
          : undefined,
      ...(meterDetails.meterReadingMonth && {
        readingMonth: Number(meterDetails.meterReadingMonth)
      })
    },
    solarPanels: meterDetails.hasSolarPanels,
    walloniaSolarPanelsSince2024:
      address.region === REGIONS.wallonia && meterDetails.counterType === COUNTER_TYPES.digital
        ? meterDetails.walloniaSolarPanelsSince2024
        : undefined
  } satisfies CreateContractRequestBody
}
